/* eslint-disable camelcase */
import { updateField } from '@state/helpers'
import { deleteNewJourneysWithLegs } from '@state/builders/journeys/delete-new-journeys-with-legs'
import createOjpJourneys from '@state/builders/journeys/create-ojp-journeys'
import roundTime from '@utils/round-time'
import { JourneyPlannerService } from '@/src/common/ApiService'
import { addHours } from 'date-fns'
import { createJourney, addJourney, deleteJourney, deleteAllJourneys } from '@state/builders/journeys/helpers'
import { createBookingOnSearch } from '@state/builders/bookings/create-booking-on-search'
import { updateJourneysOnAddLegs } from '@state/builders/journeys/update-journeys-on-add-legs'
import { addJourneyLeg, deleteAllJourneysLegs } from '@state/builders/journey-legs/helpers'
import { createBooking } from '@state/builders/bookings/create-booking'
import { getUnconfirmedBooking } from '@state/queries/bookings/get-unconfirmed-booking'
import { deleteJourneysByDirections } from '@state/builders/journeys/delete-journeys-by-directions'
import {
  UPDATE_JOURNEY_LIST,
  RESET_JOURNEY_LIST,
  RESET_JOURNEY_LIST_BY_DIRECTION,
  RESET_REPLAN_JOURNEY_LIST_BY_DIRECTION,
  UPDATE_RESULTS_LIST,
  UPDATE_ADD_LEG_RESULTS_LIST,
  UPDATE_EXISTING_ADD_LEG_RESULTS_LIST,
  RESET_JOURNEY_PLANNER,
  RESET_JOURNEY_PLANNER_BY_DIRECTION,
  RESET_EXTRA_JOURNEY_PLANNER,
  TOGGLE_LOADING,
  TOGGLE_LOADING_BY_DIRECTION,
  UPDATE_LEG_ASSISTANCE,
  UPDATE_OUTWARD_JOURNEY,
  UPDATE_INWARD_JOURNEY,
  UPDATE_QUERY,
  UPDATE_DATE,
  UPDATE_ROUTES,
  UPDATE_METADATA,
  UPDATE_METADATA_BY_DIRECTION,
  UPDATE_EXTRA_LEGS_METADATA,
  UPDATE_EXTRA_JOURNEY,
  ADD_LEGS_TO_RESULTS,
  RESET_EXTRA_JOURNEY_LIST,
  UPDATE_EMPTY_OUTWARD_INWARD,
  UPDATE_MULTI_JOURNEY_RESULTS_LIST,
  UPDATE_MULTI_JOURNEY_RESULTS_LIST_BY_DIRECTION,
  UPDATE_TAB_INDEX,
} from '@mutations'
import {
  SELECT_OUTWARD_JOURNEY,
  SELECT_INWARD_JOURNEY,
  SEARCH,
  SEARCH_BY_DIRECTION,
  CLEAR,
  CLEAR_BY_DIRECTION,
  CLEAR_PLANNER_BY_DIRECTION,
  CLEAR_REPLAN_BY_DIRECTION,
  CLEAR_EXTRA_JOURNEY_FILTER,
  CONFIRM_LEG_ASSISTANCE,
  SEARCH_QUERY_PREFILL,
  UPDATE_JOURNEY_RETURN_DATE,
  UPDATE_JOURNEY_PLANNER,
  SEARCH_ADD_LEG,
  SELECT_EXTRA_JOURNEY,
  ONE_TIME_ERROR_NOTIFICATION,
} from '@actions'
import { RESET_ADDITIONAL_OPTIONS } from '../mutations'
import { formatChangeTime } from '@utils/change-time-options'

const defaultState = () => {
  return {
    query: {
      journeyType: 'one_way',
      origin: null,
      destination: null,
      returnOrigin: null,
      returnDestination: null,
      outwardTime: {
        type: 'depart_by',
        datetime: roundTime(new Date()),
      },
      inwardTime: {
        type: 'depart_by',
        datetime: roundTime(addHours(new Date(), 1)),
      },
      realtimeEnquiry: 'STANDARD',
      route: 'via',
      returnRoute: 'via',
      routeStation: null,
      returnRouteStation: null,
      directTrains: false,
      returnDirectTrains: false,
      changeTime: { value: 0, label: 'Allow minimum time only' },
      returnChangeTime: { value: 0, label: 'Allow minimum time only' },
      routes: [],
      returnRoutes: [],
      fastestTrains: false,
      returnFastestTrains: false,
      operatorCode: null,
      returnOperatorCode: null,
    },
    addOutwardLegQuery: {
      origin: null,
      destination: null,
      time: {
        type: 'depart_by',
        datetime: roundTime(new Date()),
      },
    },
    addInwardLegQuery: {
      origin: null,
      destination: null,
      time: {
        type: 'depart_by',
        datetime: roundTime(addHours(new Date(), 1)),
      },
    },
    loading: false,
    outwardLoading: false,
    inwardLoading: false,
    extraOutwardloading: false,
    extraInwardloading: false,
    outwardResults: null,
    inwardResults: null,
    outwardJourneyIds: null,
    inwardJourneyIds: null,
    extraOutwardJourneyIds: null,
    extraInwardJourneyIds: null,
    meta: null,
    returnMeta: null,
    extraOutwardLegsMeta: null,
    extraInwardLegsMeta: null,
    outwardEmpty: false,
    inwardEmpty: false,
    tabIndexMeta: {
      inward: 0,
      outward: 0,
    },
  }
}

export const state = defaultState()

export const getters = {
  getTabIndexMeta: (state) => state.tabIndexMeta,
}

export const mutations = {
  updateField,

  [RESET_JOURNEY_PLANNER](state) {
    Object.assign(state, defaultState())
  },

  [RESET_JOURNEY_PLANNER_BY_DIRECTION](state, direction) {
    const isInward = ['return', 'inward'].includes(direction)
    const directionTime = `${isInward ? 'inward' : 'outward'}Time`
    const origin = isInward ? 'returnOrigin' : 'origin'
    const destination = isInward ? 'returnDestination' : 'destination'

    const datetime = isInward
      ? roundTime(addHours(state.query.outwardTime.datetime, 1))
      : defaultState().query[directionTime].datetime

    state.query[directionTime].type = defaultState().query[directionTime].type
    state.query[directionTime].datetime = datetime
    state.query[origin] = defaultState().query[origin]
    state.query[destination] = defaultState().query[destination]
  },

  [RESET_EXTRA_JOURNEY_PLANNER](state, { isOutward, minimumDateTime = new Date() }) {
    const queryType = isOutward ? 'addOutwardLegQuery' : 'addInwardLegQuery'
    const stateQuery = state[queryType]
    const defaultQuery = defaultState()[queryType]

    stateQuery.origin = defaultQuery.origin
    stateQuery.destination = defaultQuery.destination
    stateQuery.time = {
      type: defaultQuery.time.type,
      datetime: minimumDateTime,
    }
  },

  [RESET_JOURNEY_LIST](state) {
    deleteAllJourneys()
    deleteAllJourneysLegs()
    state.outwardJourneyIds = null
    state.inwardJourneyIds = null
  },

  [RESET_JOURNEY_LIST_BY_DIRECTION](state, direction) {
    const directions = direction === 'outward' ? ['outward', 'single'] : ['inward', 'return']

    deleteJourneysByDirections(directions)

    state[`${direction}JourneyIds`] = null
  },

  [RESET_REPLAN_JOURNEY_LIST_BY_DIRECTION](state, direction) {
    const directions = ['inward', 'return'].includes(direction) ? ['inward', 'return'] : ['outward', 'single']

    deleteNewJourneysWithLegs(directions)

    state[`${direction}JourneyIds`] = null
  },

  [RESET_EXTRA_JOURNEY_LIST](state, isOutward) {
    const direction = isOutward ? 'outward' : 'return'
    const outwardJourneyIds = state.outwardJourneyIds || []
    const inwardJourneyIds = state.inwardJourneyIds || []
    const journeyIds = outwardJourneyIds.concat(inwardJourneyIds)

    deleteJourney(
      (journey) => journey.extraJourney === true && journey.direction === direction && !journeyIds.includes(journey.id)
    )

    state[isOutward ? 'extraOutwardJourneyIds' : 'extraInwardJourneyIds'] = null
  },

  [UPDATE_JOURNEY_LIST](state, payload) {
    state.journeys = payload
  },

  [UPDATE_RESULTS_LIST](state, payload) {
    const outwardJourneys = payload.outward_journeys || []
    const inwardJourneys = payload.inward_journeys || []

    // Get current booking exist or new
    // Get current journey (if it exists) or use a new journey logic
    // Push results to Journey object to ojp_results: []

    if (outwardJourneys.length === 0) {
      deleteJourney((journey) => {
        return ['return', 'inward'].includes(journey.direction)
      })
      addJourney({
        data: inwardJourneys,
      })
    } else if (inwardJourneys.length === 0) {
      deleteJourney((journey) => {
        return ['single', 'outward'].includes(journey.direction)
      })
      addJourney({
        data: outwardJourneys,
      })
    } else {
      createJourney({
        data: [...outwardJourneys, ...inwardJourneys],
      })
    }
  },

  [UPDATE_MULTI_JOURNEY_RESULTS_LIST](state, payload) {
    const outwardJourneys = payload.outward_journeys || []
    const inwardJourneys = payload.inward_journeys || []

    createBookingOnSearch(outwardJourneys, inwardJourneys)
  },

  [UPDATE_MULTI_JOURNEY_RESULTS_LIST_BY_DIRECTION](state, { payload, direction }) {
    const journeys = payload.journeys || []
    const existingBooking = getUnconfirmedBooking()

    if (existingBooking)
      createOjpJourneys({
        bookings: [existingBooking],
        journeys,
        direction,
      })
    else
      createBooking({ data: { status: 'unconfirmed' } }).then(({ bookings }) => {
        createOjpJourneys({
          bookings,
          journeys,
          direction,
        })
      })
  },

  [UPDATE_ADD_LEG_RESULTS_LIST](state, { payload, isOutward }) {
    const direction = isOutward ? 'outward' : 'return'
    const journeys = payload.outward_journeys.map((journey) => ({
      ...journey,
      extraJourney: true,
      direction,
    }))

    addJourney({ data: journeys })
  },

  [UPDATE_EXISTING_ADD_LEG_RESULTS_LIST](state, { payload, isOutward, parentJourneyId }) {
    const direction = isOutward ? 'outward' : 'return'
    const journeys = payload.outward_journeys.map((journey) => ({
      ...journey,
      bespoke: true,
      direction,
    }))

    updateJourneysOnAddLegs(journeys, parentJourneyId)
  },

  [TOGGLE_LOADING](state, isOutward = null) {
    if (isOutward === null) state.loading = !state.loading
    else if (isOutward) state.extraOutwardloading = !state.extraOutwardloading
    else state.extraInwardloading = !state.extraInwardloading
  },

  [TOGGLE_LOADING_BY_DIRECTION](state, direction) {
    state[`${direction}Loading`] = !state[`${direction}Loading`]
  },

  [UPDATE_OUTWARD_JOURNEY](state, ids) {
    state.outwardJourneyIds = ids
  },

  [UPDATE_INWARD_JOURNEY](state, ids) {
    state.inwardJourneyIds = ids
  },

  [UPDATE_EXTRA_JOURNEY](state, { ids, isOutward }) {
    if (isOutward) state.extraOutwardJourneyIds = ids
    else state.extraInwardJourneyIds = ids
  },

  [UPDATE_QUERY](state, { journeyType, origin, destination, outwardTime, inwardTime, changeTime, returnChangeTime }) {
    state.query.journeyType = journeyType
    state.query.origin = origin
    state.query.destination = destination
    state.query.outwardTime = outwardTime
    state.query.inwardTime = inwardTime
    state.query.changeTime = formatChangeTime(changeTime)
    state.query.returnChangeTime = formatChangeTime(returnChangeTime)
  },

  [UPDATE_DATE](state, data) {
    state.query.inwardTime.datetime = roundTime(addHours(data, 1))
  },

  [UPDATE_ROUTES](state, data) {
    state.query[data.routeType][data.index] = data.route
  },

  [RESET_ADDITIONAL_OPTIONS](state, data) {
    if (data === 'one_way') {
      state.query.directTrains = false
      state.query.changeTime = null
      state.query.routes = []
      state.query.fastestTrains = false
      state.query.operatorCode = null
    } else {
      state.query.returnDirectTrains = false
      state.query.returnChangeTime = null
      state.query.returnRoutes = []
      state.query.returnFastestTrains = false
      state.query.returnOperatorCode = null
    }
  },

  [UPDATE_METADATA](state, data) {
    state.meta = data
  },

  [UPDATE_METADATA_BY_DIRECTION](state, { payload, direction }) {
    state[direction === 'outward' ? 'meta' : 'returnMeta'] = payload
  },

  [UPDATE_EXTRA_LEGS_METADATA](state, { meta, isOutward }) {
    state[isOutward ? 'extraOutwardLegsMeta' : 'extraInwardLegsMeta'] = meta
  },

  [UPDATE_EMPTY_OUTWARD_INWARD](state, payload) {
    for (const [key, value] of Object.entries(payload)) state[key] = value
  },

  [UPDATE_TAB_INDEX](state, data) {
    const journeyType = data.journeyType.toLowerCase() === 'outward' ? 'outward' : 'inward'
    state.tabIndexMeta[journeyType] = data.index
  },
}

export const actions = {
  [UPDATE_ROUTES]({ commit }, payload) {
    commit(UPDATE_ROUTES, payload)
  },

  [RESET_ADDITIONAL_OPTIONS]({ commit }, payload) {
    commit(RESET_ADDITIONAL_OPTIONS, payload)
  },

  [SEARCH]({ commit }, query) {
    const { using_earlier_later: usingEarlierLater = false } = query

    commit(TOGGLE_LOADING)
    if (!usingEarlierLater) commit(RESET_JOURNEY_LIST)
    return new Promise((resolve, reject) => {
      JourneyPlannerService.search({ ...query, include_cancelled: true })
        .then((response) => {
          const data = response.data
          const outwardJourneys = data.outward_journeys
          const inwardJourneys = data.inward_journeys
          const hasOutwardJourneys = Array.isArray(outwardJourneys) && outwardJourneys.length > 0
          const hasInwardJourneys = Array.isArray(inwardJourneys) && inwardJourneys.length > 0

          if (
            usingEarlierLater &&
            ((inwardJourneys === null && hasOutwardJourneys) || (hasInwardJourneys && hasOutwardJourneys))
          )
            commit(RESET_JOURNEY_LIST)
          if (data?.meta) commit(UPDATE_METADATA, data.meta)

          commit(UPDATE_MULTI_JOURNEY_RESULTS_LIST, data)
          commit(TOGGLE_LOADING)
          resolve(data)
        })
        .catch((error) => {
          commit(TOGGLE_LOADING)
          commit(RESET_JOURNEY_LIST)
          if (error.response) {
            reject(new Error(error.response.data?.error ?? error.response.data?.message))
          } else {
            reject(new Error('Something went wrong'))
          }
        })
    })
  },

  [SEARCH_BY_DIRECTION]({ commit }, query) {
    const { using_earlier_later: usingEarlierLater = false, direction, isReplan } = query
    const resetListAction = !usingEarlierLater
      ? isReplan
        ? RESET_REPLAN_JOURNEY_LIST_BY_DIRECTION
        : RESET_JOURNEY_LIST_BY_DIRECTION
      : null

    commit(TOGGLE_LOADING_BY_DIRECTION, direction)

    if (resetListAction) commit(resetListAction, direction)

    return new Promise((resolve, reject) => {
      JourneyPlannerService.searchByDirection({
        ...query,
        include_cancelled: true,
      })
        .then((response) => {
          const data = response.data
          const journeys = data.journeys
          const hasJourneys = Array.isArray(journeys) && journeys.length > 0

          if (usingEarlierLater && hasJourneys) {
            const resetListAction = isReplan ? RESET_REPLAN_JOURNEY_LIST_BY_DIRECTION : RESET_JOURNEY_LIST_BY_DIRECTION

            commit(resetListAction, direction)
          }

          if (data?.meta)
            commit(UPDATE_METADATA_BY_DIRECTION, {
              payload: data.meta,
              direction,
            })

          if (!usingEarlierLater || (usingEarlierLater && hasJourneys))
            commit(UPDATE_MULTI_JOURNEY_RESULTS_LIST_BY_DIRECTION, {
              payload: data,
              direction,
            })

          commit(TOGGLE_LOADING_BY_DIRECTION, direction)
          commit(UPDATE_TAB_INDEX, { journeyType: direction, index: 0 })
          resolve(data)
        })
        .catch((error) => {
          commit(TOGGLE_LOADING_BY_DIRECTION, direction)
          if (!(usingEarlierLater && error.response?.status === 422)) {
            if (isReplan) commit(RESET_REPLAN_JOURNEY_LIST_BY_DIRECTION, direction)
            else commit(RESET_JOURNEY_LIST_BY_DIRECTION, direction)
          }
          if (error.response) {
            reject(new Error(error.response.data?.error ?? error.response.data?.message))
          } else {
            reject(new Error('Something went wrong'))
          }
        })
    })
  },

  [SEARCH_ADD_LEG]({ commit }, query) {
    const { isOutward, parentJourneyId } = query
    delete query.isOutward
    delete query.parentJourneyId

    commit(TOGGLE_LOADING, isOutward)
    return new Promise((resolve, reject) => {
      JourneyPlannerService.search({ ...query, include_cancelled: true })
        .then((response) => {
          const data = response.data
          const journeys = data.outward_journeys
          const hasJourneys = Array.isArray(journeys) && journeys.length > 0

          if (data?.meta)
            commit(UPDATE_EXTRA_LEGS_METADATA, {
              meta: data.meta,
              isOutward,
            })

          if (hasJourneys) commit(RESET_EXTRA_JOURNEY_LIST, isOutward)

          if (parentJourneyId)
            commit(UPDATE_EXISTING_ADD_LEG_RESULTS_LIST, {
              payload: data,
              isOutward,
              parentJourneyId,
            })
          else
            commit(UPDATE_ADD_LEG_RESULTS_LIST, {
              payload: data,
              isOutward,
            })
          resolve(data)
        })
        .catch((error) => {
          if (error.response) {
            reject(new Error(error.response.data?.error ?? error.response.data?.message))
          } else {
            reject(new Error('Something went wrong'))
          }
        })
        .finally(() => {
          commit(TOGGLE_LOADING, isOutward)
        })
    })
  },

  [CLEAR]({ commit }) {
    commit(RESET_JOURNEY_PLANNER)
    commit(RESET_JOURNEY_LIST)
  },

  [CLEAR_BY_DIRECTION]({ commit }, direction) {
    commit(RESET_JOURNEY_PLANNER_BY_DIRECTION, direction)
    commit(RESET_JOURNEY_LIST_BY_DIRECTION, direction)
  },

  [CLEAR_PLANNER_BY_DIRECTION]({ commit }, direction) {
    commit(RESET_JOURNEY_PLANNER_BY_DIRECTION, direction)
  },

  [CLEAR_REPLAN_BY_DIRECTION]({ commit }, direction) {
    commit(RESET_JOURNEY_PLANNER_BY_DIRECTION, direction)
    commit(RESET_REPLAN_JOURNEY_LIST_BY_DIRECTION, direction)
  },

  [CLEAR_EXTRA_JOURNEY_FILTER]({ commit }, payload) {
    const { isOutward } = payload

    commit(RESET_EXTRA_JOURNEY_PLANNER, payload)
    commit(RESET_EXTRA_JOURNEY_LIST, isOutward)
  },

  [CONFIRM_LEG_ASSISTANCE]({ commit }, payload) {
    commit(UPDATE_LEG_ASSISTANCE, payload)
  },

  [SELECT_OUTWARD_JOURNEY]({ commit }, ids) {
    commit(UPDATE_OUTWARD_JOURNEY, ids)
  },

  [SELECT_INWARD_JOURNEY]({ commit }, ids) {
    commit(UPDATE_INWARD_JOURNEY, ids)
  },

  [SELECT_EXTRA_JOURNEY]({ commit }, payload) {
    commit(UPDATE_EXTRA_JOURNEY, payload)
  },

  [SEARCH_QUERY_PREFILL]({ commit }, payload) {
    commit(UPDATE_QUERY, payload)
  },

  [UPDATE_JOURNEY_RETURN_DATE]({ commit }, payload) {
    commit(UPDATE_DATE, payload)
  },

  [UPDATE_JOURNEY_PLANNER]({ commit }, payload) {
    commit(RESET_JOURNEY_LIST)
    commit(UPDATE_RESULTS_LIST, payload)
  },

  [ADD_LEGS_TO_RESULTS]({ commit }, payload) {
    const { mainJourney, newLegs, legStackName, legStackId, isOutward, minimumDateTime } = payload
    const lastLegPosition = mainJourney.legs.length

    const updatedNewLegs = newLegs.map((leg) => ({
      ...leg,
      journey_id: mainJourney.id,
      position: lastLegPosition + leg.position,
      leg_stack_name: legStackName,
      leg_stack_id: legStackId,
    }))

    addJourneyLeg({ data: updatedNewLegs })

    commit(RESET_EXTRA_JOURNEY_LIST, isOutward)
    commit(RESET_EXTRA_JOURNEY_PLANNER, { isOutward, minimumDateTime })
  },

  [ONE_TIME_ERROR_NOTIFICATION]({ commit }, payload) {
    commit(UPDATE_EMPTY_OUTWARD_INWARD, payload)
  },

  [UPDATE_TAB_INDEX]({ commit }, payload) {
    commit(UPDATE_TAB_INDEX, payload)
  },
}
